// FileActions — the file's ONE registry (ADR-036 §7 — the four kinds; ADR-048:
// the panel and a shelf row's ⋯ read the same list, so they can never drift).
//
//   • `fileActs` is the PURE registry — rows with a kind and a `show` predicate
//     over the file's status (current · an older version · in the bin) and the
//     closed-over facts (its nature, its rights); the shape test
//     (`actionGrammar.test.ts`) walks it.
//   • `resolveFileActs` resolves it for one file — the panel passes the result to
//     `ContextPanel acts` (the top row + the door, on both hosts); the shelf row
//     draws it through `toMenuRows`, Download aside (the row shows it in the open).
//
// The file has no page (the rail and the peek): the main button is Download; the
// Edit menu holds Change classification (what a photo shows — a decider's, on a
// place with units, and NOT while a decision is pending: the "What it shows"
// card's answers are the door then) · Edit caption (an app-uploaded photo — a
// capture's words are the technician's) · Rename (a document) · Move… (never a
// capture — its ticket is its place, ADR-068 §2); the door holds Copy link, then
// the lifecycle Replace with a new version (a document) · Restore (in the bin) ·
// Delete (the danger act — the anchor's writer, or a reviewer on a photo). An
// older version is read-only: Download, and Copy link in the door. A catalog copy is preview +
// download only, whoever looks (FRONTEND-PATTERNS §Files) — the law lives HERE,
// never per host. Each row is offered only where its host passes the handler.

import type { TFunction } from 'i18next';
import { resolveActs, type ActionItem, type EntityAct } from './entityRegistry.js';
import { icons } from '../ui/icons.js';
import { natureOf, type LibraryFile } from './filesDisplay.js';

export type FileStatus = 'current' | 'older' | 'deleted';

/** What each act does — the host's handlers; a row whose handler is absent is not offered. */
export interface FileActHandlers {
  download?: () => void;
  copyLink?: () => void;
  changeShows?: () => void;
  editCaption?: () => void;
  rename?: () => void;
  move?: () => void;
  replace?: () => void;
  restore?: () => void;
  remove?: () => void;
}

/** The closed-over facts the rows read beside the status. */
export interface FileActFacts {
  /** A described thing (a photo, a video, a voice note) — not a filed document. */
  media: boolean;
  /** A field capture — born on its ticket (its caption frozen at ingest, its place the ticket). */
  capture: boolean;
  /** The anchor's write right on THIS file (never on a catalog copy). */
  canWrite: boolean;
  /** The review key — a reviewer may delete a photo. */
  canReview: boolean;
  /** May bring a file back from the bin. */
  canRestore: boolean;
  /** May say what it shows here — a decider, on a place whose units the picker offers. */
  canDescribe: boolean;
  /** A decision is pending — the card's answers are the door. */
  deciding: boolean;
}

const noop = () => {};

/** The file's registry — pure: the rows, their kinds and predicates. */
export function fileActs(h: FileActHandlers, f: FileActFacts): ActionItem<FileStatus>[] {
  const current = (ctx: { status: FileStatus }) => ctx.status === 'current';
  return [
    // The primary — the act every viewer comes for (an older version keeps it, with Copy link).
    { id: 'download', kind: 'primary', labelKey: 'actions.download', icon: icons.download, run: { handler: h.download ?? noop }, gate: 'member', order: 10, show: () => Boolean(h.download) },
    // The edits — the Edit menu (the file has no page, so no card carries one).
    { id: 'change-shows', kind: 'edit', section: 'shows', labelKey: 'actions.changeShows', icon: icons.edit, run: { handler: h.changeShows ?? noop }, gate: 'member', order: 10, show: (ctx) => Boolean(h.changeShows) && current(ctx) && f.media && f.canDescribe && !f.deciding },
    { id: 'edit-caption', kind: 'edit', labelKey: 'actions.editCaption', icon: icons.edit, run: { handler: h.editCaption ?? noop }, gate: 'member', order: 20, show: (ctx) => Boolean(h.editCaption) && current(ctx) && f.canWrite && f.media && !f.capture },
    { id: 'rename', kind: 'edit', labelKey: 'actions.rename', icon: icons.edit, run: { handler: h.rename ?? noop }, gate: 'member', order: 30, show: (ctx) => Boolean(h.rename) && current(ctx) && f.canWrite && !f.media },
    { id: 'move', kind: 'edit', labelKey: 'actions.moveTo', icon: icons.folder, run: { handler: h.move ?? noop }, gate: 'member', order: 40, show: (ctx) => Boolean(h.move) && current(ctx) && f.canWrite && !f.capture },
    // The door — the operation, then the lifecycle; Delete the danger act, fenced last.
    { id: 'copy-link', kind: 'operation', labelKey: 'actions.copyLink', icon: icons.link, run: { handler: h.copyLink ?? noop }, gate: 'member', order: 10, show: () => Boolean(h.copyLink) },
    { id: 'replace', kind: 'lifecycle', labelKey: 'actions.replace', icon: icons.upload, run: { handler: h.replace ?? noop }, gate: 'member', order: 10, show: (ctx) => Boolean(h.replace) && current(ctx) && f.canWrite && !f.media },
    { id: 'restore', kind: 'lifecycle', labelKey: 'actions.restore', icon: icons.restore, run: { handler: h.restore ?? noop }, gate: 'member', order: 10, show: (ctx) => Boolean(h.restore) && ctx.status === 'deleted' && f.canRestore },
    { id: 'delete', kind: 'lifecycle', labelKey: 'actions.delete', icon: icons.delete, run: { handler: h.remove ?? noop }, gate: 'member', order: 20, tone: 'danger', show: (ctx) => Boolean(h.remove) && current(ctx) && (f.canWrite || (f.media && f.canReview)) },
  ];
}

/** Where a file stands — the status its rows read. */
export function fileStatus(file: LibraryFile): FileStatus {
  return file.deleted_at ? 'deleted' : file.superseded_by ? 'older' : 'current';
}

/** The host's per-file handlers (the library's) — each bound to the file here. */
export interface FileHostHandlers {
  onDownload?: (file: LibraryFile) => void;
  onCopyLink?: (file: LibraryFile) => void;
  onEditCaption?: (file: LibraryFile) => void;
  onRename?: (file: LibraryFile) => void;
  onMove?: (file: LibraryFile) => void;
  onReplace?: (file: LibraryFile) => void;
  onRestore?: (file: LibraryFile) => void;
  onDelete?: (file: LibraryFile) => void;
}

/** The file's acts, resolved for one file — what the panel and a shelf row draw. The rights are the
 *  container's per-file word (the panel's props; a row's `canWriteOf`); the gate is the member's, the
 *  surface being read at all. `changeShows` is the panel's own (the picker opens in place there). */
export function resolveFileActs(
  file: LibraryFile,
  h: FileHostHandlers,
  rights: { canWrite: boolean; canReview: boolean; canRestore: boolean; canDescribe?: boolean; deciding?: boolean; changeShows?: () => void },
  t: TFunction,
): EntityAct[] {
  const bind = (fn?: (file: LibraryFile) => void) => (fn ? () => fn(file) : undefined);
  return resolveActs(
    fileActs(
      {
        download: bind(h.onDownload),
        copyLink: bind(h.onCopyLink),
        changeShows: rights.changeShows,
        editCaption: bind(h.onEditCaption),
        rename: bind(h.onRename),
        move: bind(h.onMove),
        replace: bind(h.onReplace),
        restore: bind(h.onRestore),
        remove: bind(h.onDelete),
      },
      {
        media: natureOf(file) === 'media',
        capture: file.anchor.kind === 'ticket',
        // A catalog copy is preview + download only, whoever looks.
        canWrite: rights.canWrite && file.provenance !== 'catalog',
        canReview: rights.canReview,
        canRestore: rights.canRestore,
        canDescribe: rights.canDescribe ?? false,
        deciding: rights.deciding ?? false,
      },
    ),
    { can: () => true, entitlements: new Set(), status: fileStatus(file) },
    (key) => t(key),
  );
}
